import PropTypes from "prop-types";
import { ChevronDown, ChevronUp, Wallet } from "lucide-react";
import { Card } from "../ui";
import { cardLogos } from "../../utils/cardLogos";

const isCardMethod = (method) => method?.type === "credit_card";

// Local fallback logos, keyed by PaymentLinkingMethod's vendor `provider`
// code (e.g. "shopeepay", "midtrans"). Only used when the API doesn't
// return a `logo_url` for the method (payment_linking_methods.icon_url is
// currently empty in the DB for every e-wallet method) — without this the
// card silently falls back to the generic lucide Wallet icon for every
// e-wallet provider, ShopeePay included.
const FALLBACK_EWALLET_LOGOS = {
  shopeepay: "/assets/images/payment-methods/shopeepay.png",
  midtrans: "/assets/images/payment-methods/gopay.svg",
};

const resolveTitle = (method) => {
  // The API gives us a per-method label (PaymentLinkingMethod.display_name),
  // e.g. "GoPay" or "Credit/Debit Card". Fall back to a type-shaped label
  // only when the API didn't provide one.
  if (method?.name && String(method.name).trim() !== "") {
    return method.name;
  }
  if (isCardMethod(method)) return "Credit Card";
  if (method?.type === "ewallet") return "E-Wallet";
  return "Payment Method";
};

const HeaderIcon = ({ method }) => {
  // Prefer a server-provided logo URL when present (lets ops add brand assets
  // via BOSS later without an SPA deploy).
  if (method?.logo_url) {
    return (
      <img
        src={method.logo_url}
        alt={resolveTitle(method)}
        className="w-4 h-4 sm:w-5 sm:h-5 object-contain shrink-0"
      />
    );
  }

  if (isCardMethod(method)) {
    return (
      <img
        src="/assets/icons/creditcard-icon.svg"
        alt="Credit Card"
        className="w-4 h-4 sm:w-5 sm:h-5 object-contain shrink-0"
      />
    );
  }

  const fallbackLogo = FALLBACK_EWALLET_LOGOS[method?.provider];
  if (fallbackLogo) {
    return (
      <img
        src={fallbackLogo}
        alt={resolveTitle(method)}
        className="w-4 h-4 sm:w-5 sm:h-5 object-contain shrink-0"
      />
    );
  }

  // Generic e-wallet / fallback. Uses lucide so no extra asset needed.
  return <Wallet className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" style={{ color: "#3B3B3B" }} />;
};

HeaderIcon.propTypes = {
  method: PropTypes.shape({
    type: PropTypes.string,
    provider: PropTypes.string,
    logo_url: PropTypes.string,
    name: PropTypes.string,
  }),
};

const PaymentMethodCard = ({ method, isExpanded, onClick, children }) => {
  // supported_cards is only meaningful for card methods; suppressing it elsewhere
  // avoids "GoPay" rendering with VISA/Mastercard chips next to it.
  const supportedCards = isCardMethod(method)
    ? method?.supported_cards || method?.features?.supported_cards || []
    : [];

  return (
    <Card className="mb-3 overflow-hidden">
      <button
        onClick={onClick}
        className="w-full px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50 transition-colors gap-3"
      >
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <HeaderIcon method={method} />
          <div className="text-left flex-1 min-w-0">
            <p className="text-xs sm:text-sm font-semibold" style={{ color: "#3B3B3B" }}>
              {resolveTitle(method)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 sm:gap-3 shrink-0">
          {/* Supported Card Logos (card methods only) */}
          {supportedCards.length > 0 && (
            <div className="hidden sm:flex items-center gap-1.5">
              {supportedCards.slice(0, 4).map((card) => {
                const logoSrc = cardLogos[card];
                return logoSrc ? (
                  <div
                    key={card}
                    className="w-8 h-5 rounded flex items-center justify-center p-1"
                    style={{ backgroundColor: "#F2F4F7" }}
                  >
                    <img
                      src={logoSrc}
                      alt={card}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : null;
              })}
            </div>
          )}

          {isExpanded ? (
            <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" style={{ color: "#515164" }} />
          ) : (
            <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 shrink-0" style={{ color: "#515164" }} />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-gray-100">
          {children}
        </div>
      )}
    </Card>
  );
};

PaymentMethodCard.propTypes = {
  method: PropTypes.shape({
    method_id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    type: PropTypes.string.isRequired,
    logo_url: PropTypes.string,
    supported_cards: PropTypes.array,
    features: PropTypes.shape({
      supported_cards: PropTypes.array,
    }),
  }).isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node,
};

export default PaymentMethodCard;
